import { useCallback, useEffect, useState } from 'react';
import {
  clampDecimal,
  formatDecimal,
  formatDecimalForEditing,
  parseLocaleDecimal,
  sanitizeDecimalInput,
} from '../utils/localeNumber';

export interface UseLocaleDecimalInputOptions {
  value: number | string | null | undefined;
  onChange: (value: number | null) => void;
  decimalPlaces?: number;
  min?: number;
  max?: number;
}

/**
 * Money-safe decimal input: parent state updates on blur (or when leaving incomplete
 * values as null), not on every keystroke. Prevents mid-edit zeros / partial parses
 * from corrupting line pricing.
 */
export const useLocaleDecimalInput = ({
  value,
  onChange,
  decimalPlaces = 2,
  min,
  max,
}: UseLocaleDecimalInputOptions) => {
  const [displayValue, setDisplayValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (isFocused) return;

    if (value == null || value === '') {
      setDisplayValue('');
      return;
    }

    setDisplayValue(formatDecimal(value, decimalPlaces));
  }, [value, isFocused, decimalPlaces]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    setDisplayValue(formatDecimalForEditing(value, decimalPlaces));
  }, [value, decimalPlaces]);

  const commitDisplayValue = useCallback(
    (raw: string) => {
      if (raw === '' || raw === ',' || raw.endsWith(',')) {
        onChange(null);
        return null;
      }
      const parsed = parseLocaleDecimal(raw);
      const clamped = clampDecimal(parsed, min, max);
      onChange(clamped);
      return clamped;
    },
    [min, max, onChange],
  );

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    const clamped = commitDisplayValue(displayValue);
    setDisplayValue(clamped != null ? formatDecimal(clamped, decimalPlaces) : '');
  }, [commitDisplayValue, decimalPlaces, displayValue]);

  const handleChange = useCallback(
    (nextRawValue: string) => {
      const sanitized = sanitizeDecimalInput(nextRawValue, decimalPlaces);
      setDisplayValue(sanitized);
      // Commit only complete values while typing (no trailing comma / empty).
      // Incomplete input does not push 0 into parent pricing state.
      if (sanitized !== '' && !sanitized.endsWith(',')) {
        const parsed = parseLocaleDecimal(sanitized);
        if (parsed != null) {
          onChange(clampDecimal(parsed, min, max));
        }
      }
    },
    [decimalPlaces, min, max, onChange],
  );

  return {
    displayValue,
    isFocused,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onChange: handleChange,
  };
};
